<template>
  <div id="page-commercial-product-form">
    <vs-row>
      <vs-col :class="readonly ? 'readonly-form' : ''">
        <card-page
            :card-title="cardTitle"
            :switch-models="switchModels"
        >
          <vue-element-loading :active="busy"></vue-element-loading>
          <vue-form-generator
              ref="form"
              :model="model"
              :options="formOptions"
              :schema="schema"
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>

import FormModelMixin from "@/components/mixins/FormModelMixin";
import CardPage from '../../components/Views/CardPage';

const selectOptions = {
  showLabels: false,
  key: "id",
  trackBy: "id",
  label: "name",
};

export default {
  name: "CommercialProductForm",
  components: {
    CardPage,
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  mixins: [FormModelMixin],
  data() {
    return {
      model: {
        CommercialProductName: null,
        CommercialProductCode: null,
        ProductOwner_FK: null,
        Product_FK: null,
        ParentCommercialProduct_FK: null,
        Wording: null,
        StandardClause: null,
        ProductCategory_RiskCode_FK: null,
        ProductCategory_SubRiskCode_FK: null,
        SalesCountryType_FK: null,
      },
      schema: {
        groups: [
          {
            legend: 'General',
            styleClasses: "w-full",
            fields: [
              {
                disabled: this.readonly,
                type: "input",
                inputType: "text",
                styleClasses: "w-1/3 test-class-three",
                label: "Commercial Product Name",
                model: "CommercialProductName",
                required: !this.readonly,
                validator: ["required", 'checkEmptyInput'],
              },
              {
                disabled: this.readonly,
                type: "input",
                inputType: "text",
                styleClasses: "w-1/3",
                label: "Commercial Product Code",
                model: "CommercialProductCode",
                required: !this.readonly,
                validator: ["required", 'checkEmptyInput'],
              },
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-1/3",
                label: "Product",
                model: "Product_FK",
                required: !this.readonly,
                validator: "selectRequired",
                values: () => this.productDescriptionOptions.map(( product ) => {
                  return {
                    id: product.id,
                    name: `${product.ProductCode} - ${product.name}`,
                  }
                }),
                selectOptions,
              },
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-1/3",
                label: "Product Owner",
                model: "ProductOwner_FK",
                required: !this.readonly,
                validator: "selectRequired",
                values: () => this.productOwnerOptions,
                selectOptions: {
                  ...selectOptions,
                  label: "name",
                },
              },
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-1/3",
                label: "Nodes",
                model: "CommercialProductNode_FK",
                required: !this.readonly,
                validator: "selectRequired",
                values: () => this.commercialProductNodeOptions,
                selectOptions,
              },
              {
                type: "label",
                styleClasses: "w-1/6",
                label: "Wording",
                model: "Product_FK.WordingCode",
              },
              {
                type: "label",
                styleClasses: "w-1/6",
                label: "Standard Clause",
                model: "Product_FK.StandardClause",
              },
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-1/3",
                label: "Risk Class",
                model: "ProductCategory_RiskCode_FK",
                required: !this.readonly,
                validator: "required",
                values: () => this.riskCodeOptions,
                selectOptions: {
                  ...selectOptions,
                  allowEmpty: true,
                },
              },
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-1/3",
                label: "Sub Risk Class",
                model: "ProductCategory_SubRiskCode_FK",
                values: () => this.subRiskCodeOptions,
                required: !this.readonly,
                validator: "required",
                selectOptions: {
                  ...selectOptions,
                  allowEmpty: true,
                },
              },
              {
                type: 'vueMultiSelect',
                styleClasses: 'w-1/3',
                label: 'Sales Country',
                model: 'SalesCountryType_FK',
                disabled: () => this.readonly,
                values: () => this.salesCountryOptions,
                selectOptions,
                required: () => !this.readonly,
                validator: 'required'
              },
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-1/3",
                label: "Parent Commercial Product",
                model: "ParentCommercialProduct_FK",
                values: () => this.parentProductDescriptionOptions.map(( commercialProduct ) => {
                  return {
                    id: commercialProduct.id,
                    CommercialProductName: `${commercialProduct.CommercialProductCode} - ${commercialProduct.CommercialProductName}`,
                  }
                }),
                selectOptions: {
                  ...selectOptions,
                  label: "CommercialProductName",
                },
              },
            ]
          },
          {
            legend: 'Claim Notification',
            styleClasses: "w-full",
            fields: [
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-1/3",
                label: "Commercial Claims",
                model: "ClaimNotification_FK",
                values: () => this.commercialClaimsOptions,
                selectOptions: {
                  ...selectOptions,
                },
              },
            ],
          },
        ],
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
      wordings: [],
      productDescriptionOptions: [],
      productOwnerOptions: [],
      commercialProductNodeOptions: [],
      parentProductDescriptionOptions: [],
      riskCodeOptions: [],
      subRiskCodeOptions: [],
      productBundleOptions: [],
      commercialClaimsOptions: [],
      salesCountryOptions: [],
      formReferences: [
          'form',
      ],
      dynamicProducts: [],
      options: [
        {
          endpoint: () => this.$api.products.list(),
          optionsFieldName: "productDescriptionOptions",
          nameFieldName: "ProductDescription",
          idFieldName: 'Product_FK',
          additionalFields: [
            'StandardClause',
            'WordingCode',
            'ProductCode',
          ],
        },
        {
          endpoint: () => this.$api.commercialProducts.nodesList(),
          optionsFieldName: "commercialProductNodeOptions",
          nameFieldName: "name",
          idFieldName: 'CommercialProductNode_FK',
        },
        {
          endpoint: () => this.$api.commercialProducts.list(),
          optionsFieldName: "parentProductDescriptionOptions",
          nameFieldName: "ProductDescription",
          idFieldName: 'ParentCommercialProduct_FK',
          additionalFields: [
            'CommercialProductCode',
            'CommercialProductName',
          ],
        },
        {
          endpoint: () => this.$api.productCategory.riskClasses(),
          optionsFieldName: "riskCodeOptions",
          nameFieldName: "ProductCategoryName",
          idFieldName: 'ProductCategory_RiskCode_FK',
        },
        {
          endpoint: () => this.$api.productCategory.subRiskClasses(),
          optionsFieldName: "subRiskCodeOptions",
          nameFieldName: "ProductCategoryName",
          idFieldName: 'ProductCategory_SubRiskCode_FK',
        },
        {
          endpoint: () => this.$api.productOwners.list(),
          optionsFieldName: "productOwnerOptions",
          nameFieldName: "Name",
          idFieldName: 'ProductOwner_FK',
        },
        {
          endpoint: () => this.$api.productBundles.list(),
          optionsFieldName: 'productBundleOptions',
          nameFieldName: 'OrionBundleName',
        },
        {
          endpoint: () => this.$api.commercialClaims.list(),
          optionsFieldName: "commercialClaimsOptions",
          nameFieldName: "CommercialClaimNotificationName",
          idFieldName: 'ClaimNotification_FK',
        },
        {
          endpoint: () => this.$api.commercialProducts.salesCountries(),
          optionsFieldName: 'salesCountryOptions',
          nameFieldName: 'Type',
          idFieldName: 'SalesCountryType_FK'
        },
      ],
    };
  },
  computed: {
    switchModels() {
      if (!this.model) {
        return [];
      }

      return [
        {
          model: this.model.IsOris,
          activeLabel: 'ORIS',
          inactiveLabel: 'Non-ORIS',
        },
        {
          model: this.model.IsEnabled,
          activeLabel: 'Active',
          inactiveLabel: 'Inactive',
        }
      ];
    }
  },
  async created() {
    await this.setDynamicFields();
    await this.loadOpts();
  },
  methods: {
    setPreSelectedDynamicFields() {
      const dynamicProducts = this.value.dynamic_products;

      if (!dynamicProducts) {
        return;
      }

      dynamicProducts.forEach((dynamicProduct) => {
        let modelFieldName = dynamicProduct.type.Type.toLowerCase();
        modelFieldName = modelFieldName.replace(/\s/g, '');

        const selectedItem = {
          id: dynamicProduct.DynamicLoader_PK,
          name: dynamicProduct.LoaderName
        };
        this.$set(this.model, modelFieldName, selectedItem)
      });
      
      const dynamicBundles = this.value.dynamic_bundles;

      if (!dynamicBundles) {
        return;
      }

      dynamicBundles.forEach((dynamicBundle) => {
        let modelFieldName = dynamicBundle.type.Type.toLowerCase();
        modelFieldName = modelFieldName.replace(/\s/g, '');
        modelFieldName = `bundle-${modelFieldName}`;

        const selectedItem = {
          id: dynamicBundle.ProductBundle_FK,
          name: dynamicBundle.product_bundle.OrionBundleName
        };

        this.$set(this.model, modelFieldName, selectedItem)
      });
    },
    postOptions() {
      return new Promise(resolve => {
        this.setPreSelectedDynamicFields();
        resolve();
      })
    },
    async setDynamicFields() {
      await this.setProductBundles();
      await this.setDynamicDocuments();
    },
    async setDynamicDocuments() {
      const { data } = await this.$api.commercialProducts.getDynamicProductsTypes()

      let dynamicFields = data.data;

      if (dynamicFields.length > 0) {
        const fields = dynamicFields.map((field) => {
          let modelName = field.Type.toLowerCase();
          modelName = modelName.replace(/\s/g, '');

          this.$emit('add-dynamic-product', modelName);

          // Add this in for multiple option dropdowns
          this.dynamicProducts.push({
            field: modelName,
            options: [],
          })

          // Options field name is a reference to the actual object
          this.setDynamicProductOfType(field.id, () =>
              this.dynamicProducts.find(({field}) => field === modelName)
          );

          return {
            disabled: this.readonly,
            type: "vueMultiSelect",
            inputType: "text",
            styleClasses: "w-1/3",
            label: field.Type,
            model: modelName,
            // This is also a reference to the object
            values: () => this.dynamicProducts.find(({field}) => field === modelName).options,
            selectOptions: {
              ...selectOptions,
              allowEmpty: true,
            },
          };
        })

        this.schema.groups.push({
          legend: 'Documents',
          styleClasses: "w-full",
          fields: fields
        })
      }
    },
    async setProductBundles() {
      const { data } = await this.$api.commercialProducts.getProductBundleTypes()

      let productBundleFields = data.data;

      if (productBundleFields.length > 0) {
        const bundleFields = productBundleFields.map((field) => {
          let modelName = field.Type.toLowerCase();
          modelName = modelName.replace(/\s/g, '');
          modelName = `bundle-${modelName}`;

          this.$emit('add-dynamic-product', modelName);

          // Add this in for multiple option dropdowns
          this.dynamicProducts.push({
            field: modelName,
            options: [],
          })
          
          return {
            disabled: this.readonly,
            type: "vueMultiSelect",
            inputType: "text",
            styleClasses: "w-1/3",
            label: field.Type,
            model: modelName,
            values: () => this.productBundleOptions,
            selectOptions: {
              ...selectOptions,
              allowEmpty: true,
            },
          };
        })

        this.schema.groups.push({
          legend: 'Bundles',
          styleClasses: "w-full",
          fields: bundleFields
        })
      }
    },
    /**
     *
     * @param id of dynamic product
     * @param optionsFieldName
     */
    setDynamicProductOfType(id, optionsFieldName) {
      this.options.push({
        endpoint: () => this.$api.productDynamic.listByType(id),
        optionsFieldName: optionsFieldName,
        nameFieldName: 'LoaderName',
        idFieldName: 'DynamicLoader_PK',
      });
    }
  },
};
</script>

<style scoped>

</style>
